import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  Bell,
  Sun,
  Moon,
  Menu,
  X,
  User,
  LogOut,
  Settings,
  MessageSquare,
  ChevronDown,
} from 'lucide-react'
import { useThemeStore } from '@/stores/themeStore'
import { useAuthStore } from '@/stores/authStore'
import { useSocialStore } from '@/stores/socialStore'
import { useNewsStore } from '@/stores/newsStore'
import { getTierForPoints } from '@/lib/constants'

export default function Header() {
  const theme = useThemeStore((s) => s.theme)
  const toggleTheme = useThemeStore((s) => s.toggleTheme)
  const { isAuthenticated, profile, signOut } = useAuthStore()
  const unreadCount = useSocialStore((s) => s.unreadCount)
  const categories = useNewsStore((s) => s.categories)

  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Close user menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [searchOpen])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
      setSearchQuery('')
      setSearchOpen(false)
    }
  }

  const handleSignOut = async () => {
    await signOut()
    setUserMenuOpen(false)
    navigate('/login')
  }

  const tierInfo = profile ? getTierForPoints(profile.points) : null

  return (
    <header className="sticky top-0 z-50 glass-dark">
      <div className="max-w-7xl mx-auto px-4">
        {/* Main nav bar */}
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo + hamburger */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="lg:hidden p-2 rounded-lg transition-smooth-fast"
              style={{ color: 'var(--text-secondary)' }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>

            <Link to="/" className="flex items-center gap-2 group">
              <span
                className="text-2xl font-bold tracking-tight"
                style={{ fontFamily: 'var(--font-serif)', color: 'var(--text-primary)' }}
              >
                Playo<span style={{ color: 'var(--accent)' }}>News</span>
              </span>
            </Link>
          </div>

          {/* Center: Search bar (desktop) */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <form onSubmit={handleSearch} className="relative w-full">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--text-tertiary)' }}
              />
              <input
                type="text"
                placeholder="Search news..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 transition-smooth"
                style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--rule)',
                  borderRadius: 'var(--radius-sm)',
                  // @ts-expect-error custom property
                  '--tw-ring-color': 'var(--accent)',
                }}
              />
            </form>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {/* Mobile search toggle */}
            <button
              type="button"
              className="md:hidden p-2 rounded-lg transition-smooth-fast"
              style={{ color: 'var(--text-secondary)' }}
              onClick={() => setSearchOpen(!searchOpen)}
              aria-label="Search"
            >
              <Search size={20} />
            </button>

            {/* Theme toggle */}
            <motion.button
              type="button"
              whileTap={{ scale: 0.9, rotate: 180 }}
              transition={{ duration: 0.3 }}
              className="p-2 rounded-lg transition-smooth-fast"
              style={{ color: 'var(--text-secondary)' }}
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </motion.button>

            {isAuthenticated && profile ? (
              <>
                {/* Notifications */}
                <Link
                  to="/notifications"
                  className="relative p-2 rounded-lg transition-smooth-fast"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1"
                      style={{ backgroundColor: 'var(--error)' }}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </motion.span>
                  )}
                </Link>

                {/* Messages */}
                <Link
                  to="/messages"
                  className="hidden sm:block p-2 rounded-lg transition-smooth-fast"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <MessageSquare size={20} />
                </Link>

                {/* User menu */}
                <div ref={userMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 p-1.5 rounded-lg transition-smooth-fast"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <div className="w-8 h-8 rounded-full overflow-hidden ring-2" style={{
                      ringColor: tierInfo?.color || 'var(--accent)',
                      borderColor: tierInfo?.color || 'var(--accent)',
                      border: `2px solid ${tierInfo?.color || 'var(--accent)'}`,
                    }}>
                      {profile.avatar_url ? (
                        <img
                          src={profile.avatar_url}
                          alt={profile.username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center text-sm font-bold"
                          style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }}
                        >
                          {profile.username[0]?.toUpperCase()}
                        </div>
                      )}
                    </div>

                    {/* Points badge */}
                    <span
                      className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: `${tierInfo?.color}20`,
                        color: tierInfo?.color,
                      }}
                    >
                      {tierInfo?.icon} {(profile.points / 1000).toFixed(0)}k
                    </span>

                    <ChevronDown size={14} style={{ color: 'var(--text-tertiary)' }} />
                  </button>

                  {/* Dropdown */}
                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.96 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-56 overflow-hidden"
                        style={{
                          zIndex: 100,
                          backgroundColor: 'var(--bg-secondary)',
                          border: '1px solid var(--rule)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        <div className="p-3 border-b" style={{ borderColor: 'var(--border-primary)' }}>
                          <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                            {profile.display_name || profile.username}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                            @{profile.username}
                          </p>
                        </div>
                        <div className="py-1">
                          <Link
                            to={`/profile/${profile.username}`}
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 text-sm transition-smooth-fast"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            <User size={16} /> Profile
                          </Link>
                          <Link
                            to="/messages"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 text-sm transition-smooth-fast sm:hidden"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            <MessageSquare size={16} /> Messages
                          </Link>
                          <Link
                            to="/settings"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 text-sm transition-smooth-fast"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            <Settings size={16} /> Settings
                          </Link>
                          <button
                            type="button"
                            onClick={handleSignOut}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-smooth-fast"
                            style={{ color: 'var(--error)' }}
                          >
                            <LogOut size={16} /> Sign Out
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium rounded-lg transition-smooth-fast"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 text-sm font-semibold transition-smooth-fast"
                  style={{
                    color: 'white',
                    backgroundColor: 'var(--accent)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-hover)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent)' }}
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Category tabs */}
        <nav className="hidden lg:flex items-center gap-1 pb-2 overflow-x-auto no-scrollbar -mx-1">
          <Link
            to="/"
            className="px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-smooth-fast"
            style={{ color: 'var(--accent)' }}
          >
            All
          </Link>
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-smooth-fast"
              style={{ color: 'var(--text-secondary)' }}
            >
              {cat.name}
            </Link>
          ))}
        </nav>
      </div>

      {/* Mobile search overlay */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t overflow-hidden"
            style={{ borderColor: 'var(--border-primary)' }}
          >
            <form onSubmit={handleSearch} className="p-3">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--text-tertiary)' }}
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search news..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 transition-smooth"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t overflow-hidden"
            style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)' }}
          >
            <nav className="flex flex-wrap gap-2 p-4">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="px-3 py-1.5 text-sm font-medium rounded-full transition-smooth-fast"
                style={{ color: 'var(--accent)', backgroundColor: 'var(--accent-soft)' }}
              >
                All
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-3 py-1.5 text-sm font-medium rounded-full transition-smooth-fast"
                  style={{
                    color: 'var(--text-secondary)',
                    backgroundColor: 'var(--bg-tertiary)',
                  }}
                >
                  {cat.name}
                </Link>
              ))}
            </nav>

            {isAuthenticated && (
              <div className="border-t px-4 py-3 flex flex-col gap-2" style={{ borderColor: 'var(--border-primary)' }}>
                <Link
                  to="/notifications"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 py-2 text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <Bell size={16} /> Notifications
                  {unreadCount > 0 && (
                    <span
                      className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: 'var(--error)' }}
                    >
                      {unreadCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/messages"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 py-2 text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <MessageSquare size={16} /> Messages
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 py-2 text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <Settings size={16} /> Settings
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
